import {Component, EventEmitter, Input, Output, OnDestroy, OnInit, SimpleChanges, ChangeDetectorRef} from '@angular/core';
import { DataSharingService } from '../../../services/data-sharing.service';
import { CompanyService } from '../../../services/company.service';
import { QuotaManagementService } from '../../../services/quota-management.service';
import { pluralMappings } from '../../../classes/i18n';
import { MessageService } from '../../../services/message.service';
import { HttpParams } from '@angular/common/http';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';

declare var $: any;

@Component({
  selector: 'magic-insights-generation',
  templateUrl: './magic-insights-generation.component.html',
  styleUrls: ['./magic-insights-generation.component.css'],
  standalone: false,
})
export class MagicInsightsGenerationComponent implements OnInit, OnDestroy {
  @Input() modulesInfo: Record<string, any>[];
  @Input() numberOfCompaniesMatchingSearchParams: number = null;
  searchParams: HttpParams = null;
  companyIds: number[] = null;

  selectedMagicModuleIds: number[] = [];
  selectedModuleId: number = null;
  pluralMappings = pluralMappings;
  maximumCompaniesToGenerateViaQuery = 50000;
  selectedMagicInsightsGenerateCount = null;

  // --- New Quota Properties ---
  monthlyQuotaDetails: any = null;
  bulkQuotaDetails: any = null;
  loadingQuotas = false;
  hasFetchedQuotas = false; // Ensures we only call once
  // ----------------------------

  @Output() onGenerate = new EventEmitter();

  constructor(
    private dataStore: DataSharingService,
    private companyService: CompanyService,
    private messageService: MessageService,
    private data: DataSharingService,
    private quotaManagementService: QuotaManagementService,
    private cd: ChangeDetectorRef
  ) {}

  loadingState = this.companyService.isLoading;

  ngOnInit() {
    this.data.currentSelectedCompanyIds.subscribe(
      companyIds => (this.companyIds = companyIds)
    );
    this.data.currentSelectedSearchParams.subscribe(
      searchParams => (this.searchParams = searchParams)
    );
    this.handleSelectedModuleIdsChange();

    $('#magicInsightsGenerationModal').on('show.bs.modal', () => {
      if (!this.hasFetchedQuotas){
        this.fetchMagicInsightsQuotas();
      }
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    // Because this can change AFTER the component is initiatlised.
    if (changes.modulesInfo && this.modulesInfo && this.modulesInfo.length > 0) {
      if (!this.selectedModuleId) {
         this.selectedModuleId = this.modulesInfo.length === 1 ? this.modulesInfo[0].id : null;
         this.handleSelectedModuleIdsChange();
      }
    }
  }


  fetchMagicInsightsQuotas() {
    // Only run if we haven't fetched yet
    if (this.hasFetchedQuotas) return;

    this.loadingQuotas = true;
    const featureName = 'Magic Insights Answer';

    // forkJoin runs both requests in parallel and waits for both to complete
    forkJoin({
      monthly: this.quotaManagementService.getUserQuotaDetails(featureName, 'MONTHLY'),
      bulk: this.quotaManagementService.getUserQuotaDetails(featureName, 'BULK')
    }).subscribe({
      next: (results) => {
        this.monthlyQuotaDetails = results.monthly;
        this.bulkQuotaDetails = results.bulk;
        this.hasFetchedQuotas = true;
        this.loadingQuotas = false;
        this.cd.detectChanges();
      },
      error: (err) => {
        this.loadingQuotas = false;
        this.cd.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
  }

  handleSelectedModuleIdsChange() {
    this.selectedMagicModuleIds = this.selectedModuleId ? [this.selectedModuleId] : null;
  }

  confirmGenerateMIForSelectedCompanies(): void {
    const idsToSend = this.companyIds && this.companyIds.length > 0 ? this.companyIds : null;

    this.companyService
      .generateMagicInsights(
        this.selectedMagicModuleIds[0],
        this.searchParams,
        idsToSend,
        this.selectedMagicInsightsGenerateCount,
        this.numberOfCompaniesMatchingSearchParams
      )
      .subscribe(response => {
        if (response.body?.aira_run_id) {
          $('#magicInsightsGenerationModal').modal('hide');
          this.messageService.showInfo(
              'Request submitted successfully. Results may take some time to populate. You can monitor progress by visiting "Magic Insights"',
          );
          this.hasFetchedQuotas = false;  // Once we have submitted then this will refresh the quotas.
          this.onGenerate.emit();
        }
      });
  }


  get isManualSelectionMode(): boolean {
    return this.companyIds && this.companyIds.length > 0;
  }

  get showUnlockBySearchMessage(): boolean {
    if (this.isManualSelectionMode) return false;

    const isResultCountManageable =
      this.numberOfCompaniesMatchingSearchParams < this.maximumCompaniesToGenerateViaQuery ||
      this.numberOfCompaniesMatchingSearchParams === null;

    return !!this.searchParams && isResultCountManageable;
  }

  get showSelectionWarning(): boolean {
    if (this.isManualSelectionMode) return false;

    const isResultCountOverLimit = this.numberOfCompaniesMatchingSearchParams > this.maximumCompaniesToGenerateViaQuery;
    const isSelectedCountInvalid = this.selectedMagicInsightsGenerateCount === null || this.selectedMagicInsightsGenerateCount > this.maximumCompaniesToGenerateViaQuery;

    return !!this.searchParams && isResultCountOverLimit && isSelectedCountInvalid;
  }

  get canConfirmGeneration(): boolean {
    if (this.loadingState.loadingGenerateMagicInsights) {
      return false;
    }

    if (this.isManualSelectionMode) {
      return this.numberOfCompaniesMatchingSearchParams > 0;
    }

    if (this.searchParams) {
      const isResultCountValid =
        this.numberOfCompaniesMatchingSearchParams !== null &&
        this.numberOfCompaniesMatchingSearchParams <= this.maximumCompaniesToGenerateViaQuery;

      const isSelectedCountValid =
        this.selectedMagicInsightsGenerateCount !== null &&
        this.selectedMagicInsightsGenerateCount <= this.maximumCompaniesToGenerateViaQuery;

      return isResultCountValid || isSelectedCountValid;
    }

    return false;
  }
}
